<template>
  <div class="card">
    <div class="card__header header">
      <div class="header__title">
        {{ card.title }}
      </div>
      <div class="header__search">
        <input
          v-model="searchQuery"
          placeholder="Search apps..."
          class="search-input"
          @input="filterApps"
        />
      </div>
      <div
          class="header__refresh"
          :class="{ 'header__refresh--loading': isRefreshingAll }"
          :title="isRefreshingAll ? 'Refreshing...' : 'Refresh all icons'"
          @click="updateAllIcons"
      ></div>
      <div
          class="header__button"
          @click="addApp"
      ></div>
    </div>
    <div class="card__content content dedicated-table">
      <template v-if="isLoaded">
        <div
            v-for="(app, appIndex) in filteredApps"
            :key="appIndex"
            class="content__app app"
            @click="editApp(getOriginalIndex(app))"
        >
          <div
              :style="{
                        backgroundImage: `url(${app.logo_small})`,
                    }"
              class="app__logo"
          ></div>
          <div class="app__name">
            {{ app.title }}
          </div>
          <div class="app__bundle-id">
            {{ app.bundle_id }}
          </div>
          <div
              class="app__appstore-link"
              @click.stop="updateInAppStore(app)"
          >
            <template v-if="app.os ==='ios'">
              Update in AppStore
            </template>
            <template v-else>Update in GooglePlay</template>
          </div>
          <div
              v-if="app.url"
              class="app__appstore-link"
              @click.stop="viewInStore(app.url)"
          >
            <template v-if="app.os ==='ios'">
            view in AppStore
            </template>
            <template v-else> view in GooglePlay</template>
          </div>
        </div>
      </template>
      <template v-else>
        <VLoadingRow
            v-for="number in expectedAppsAmount"
            :key="number"
            height="21px"
            mb="10px"
        />
      </template>
    </div>
  </div>
</template>

<script>
import VLoadingRow from '../../../components/VLoadingRow';
import {getProjects, updateProjectByBundleId} from '../../../api/apiServices';
import apiApps from '../../../api/apiApps';

export default {
  name: 'Card',
  components: {
    VLoadingRow,
  },
  data() {
    return {
      expectedAppsAmount: getProjects().length,
      searchQuery: '',
      filteredApps: [],
      isRefreshingAll: false,
    };
  },
  props: {
    card: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    cardIndex: {
      type: Number,
      required: true,
    },
    isLoaded: {
      type: Boolean,
      required: true,
    },
    searchFields: {
      type: Array,
      default: () => ['title', 'bundle_id'],
    },
  },
  created() {
    this.filteredApps = this.card.apps || [];
  },
  watch: {
    'card.apps': {
      handler() {
        this.filterApps();
      },
      deep: true,
    },
  },
  methods: {
    async updateAllIcons() {
      if (this.isRefreshingAll) return;
      this.isRefreshingAll = true;
      const apps = this.card.apps || [];
      for (const app of apps) {
        await this.updateInAppStore(app);
      }
      this.isRefreshingAll = false;
      this.$toastr.s('All icons updated');
    },
    async updateInAppStore(app) {
      await apiApps.getAppLogo(app, app.os !== "android").then((response) => {
        if (response.results.length) {
          app.logo_large = response.results[0].artworkUrl512;
          app.logo_small = response.results[0].artworkUrl100;
        }
      }).catch(() => {
        // this.$toastr.e(reject);
      });

      const sendObj = {
        title: app.title,
        bundle_id: app.bundle_id,
        secret: app.secret,
        smallbiz: app.smallbiz,
        logo_large: app.logo_large,
        logo_small: app.logo_small,
      };
      apiApps.updateApp(sendObj, app.id).then(() => {
        const newProject = _.cloneDeep(app);
        newProject.id = '';
        delete newProject.secret;
        delete newProject.smallbiz;
        delete newProject.createdAt;
        delete newProject.store_id;
        updateProjectByBundleId(newProject);
      }).catch((error) => {
        this.$toastr.longError(
            `Update error : ${error.response.data.error}`,
        );
      });
    },
    viewInStore(href) {
      window.open(href);
    },
    addApp() {
      this.$emit('addApp', this.cardIndex);
    },
    editApp(appIndex) {
      this.$emit('editApp', this.card.apps[appIndex], appIndex, this.cardIndex);
    },
    getOriginalIndex(app) {
      return this.card.apps.findIndex(originalApp => originalApp.id === app.id);
    },
    filterApps() {
      if (!this.searchQuery.trim()) {
        this.filteredApps = this.card.apps || [];
        return;
      }
      
      const query = this.searchQuery.toLowerCase().trim();
      this.filteredApps = (this.card.apps || []).filter(app => 
        this.searchFields.some(field => 
          app[field] && app[field].toString().toLowerCase().includes(query)
        )
      );
    },
  },
};
</script>

<style
    scoped
    lang="scss"
>
@import "../../../scss/base/colors";
@import "../../../scss/base/shadow";

.card {
  width: 40%;
  min-width: 530px;
  min-height: 136px;
  height: fit-content;
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 25px;
  margin-right: 25px;
  @include dark-shadow;

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 19px;

    &__title {
      font-size: 16px;
      line-height: 20px;
      font-weight: 700;
    }

    &__search {
      flex-grow: 1;
      margin: 0 20px;
      display: flex;
      justify-content: flex-end;
      
      .search-input {
        width: 150px;
        padding: 6px 10px;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        font-size: 12px;
        outline: none;
        box-sizing: border-box;
        
        &:focus {
          border-color: $primary-color;
        }
        
        &::placeholder {
          color: #999;
        }
      }
    }

    &__refresh {
      width: 13px;
      height: 13px;
      cursor: pointer;
      margin-right: 10px;
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2302DFB0' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='23 4 23 10 17 10'/%3E%3Cpolyline points='1 20 1 14 7 14'/%3E%3Cpath d='M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15'/%3E%3C/svg%3E");

      &--loading {
        animation: spin 1s linear infinite;
        cursor: not-allowed;
        opacity: 0.6;
      }
    }

    &__button {
      width: 13px;
      height: 13px;
      cursor: pointer;
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
      background-image: url('data:image/svg+xml;base64,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');
    }
  }

  .content {
    display: flex;
    flex-direction: column;

    .app {
      display: grid;
      grid-template-columns: 20px 2.1fr 1fr 1fr 1fr;
      grid-column-gap: 20px;
      font-size: 12px;
      cursor: pointer;
      margin-bottom: 10px;

      &:last-child {
        margin-bottom: 0;
      }

      &__logo {
        border: 1px solid $primary-color;
        width: 20px;
        height: 20px;
        border-radius: 4px;
        background-size: contain;
        background-position: center;
        background-repeat: no-repeat;
        margin-right: 20px;
      }

      &__name {
        display: flex;
        width: fit-content;
        align-items: center;
        color: $dark-blue-color;
        font-weight: 700;
        text-align: left;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      &__bundle-id {
        display: block;
        padding-top: 5px;
        align-items: center;
        color: $dark-blue-color;
        text-align: left;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        //max-width: 200px;
      }

      &__appstore-link {
        display: flex;
        align-items: center;
        color: $primary-color;
        text-decoration: underline;
        justify-content: flex-end;
      }
    }
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
</style>
